import { zodResolver } from '@hookform/resolvers/zod';
import Grid2 from '@mui/material/Unstable_Grid2';
import { SubmitHandler, useForm } from 'react-hook-form';

import Button, {
  ButtonSize,
  ButtonVariant,
} from '@/components/BaseLibrary/Button/Button';
import FormInput, {
  InputType,
} from '@/components/BaseLibrary/FormInput/FormInput';
import { RadioGroup } from '@/components/BaseLibrary/RadioGroup/RadioGroup';
import GridContainer2 from '@/components/Layout/GridContainer2/GridContainer2';
import {
  landerQualifyStepSchema,
  LanderQualifyStepSchemaType,
  LanderSchemaType,
} from '@/components/TopLevelComponents/PaidLander/PaidLander';
import { OwnHome } from '@/hooks/useSubmitLeadToZeus';
import useViewPortSize from '@/hooks/useViewportSize';
import useGlobalStyles from '@/styles/global.styles';
import { setImgWidth, setSrcSetImg } from '@/utils/frontify';
import { InputName } from '@/utils/validation/inputs';

import useStyles from './QualifyStep.styles';

export interface QualifyStepProps {
  heading?: string;
  subheading?: string;
  hideImageOnMobile?: boolean;
  image?: string;
  altText?: string;
  submitText?: string;
  showHomeownerQuestion?: boolean;
  isOnDark: boolean;
  nextPage: (data: LanderQualifyStepSchemaType) => void;
  show: boolean;
  zipCodeLabel: string;
  averageElectricBillLabel: string;
  averageElectricBillPlaceholder: string;
  ownYourHomeLabel: string;
  reverseLayout?: boolean;
  fixedGrid?: boolean;
}

export default function QualifyStep({
  heading,
  subheading,
  hideImageOnMobile = false,
  image,
  altText,
  submitText,
  showHomeownerQuestion,
  isOnDark,
  nextPage,
  show,
  zipCodeLabel,
  averageElectricBillLabel,
  averageElectricBillPlaceholder,
  ownYourHomeLabel,
  reverseLayout,
  fixedGrid,
}: QualifyStepProps) {
  const {
    register,
    trigger,
    handleSubmit,
    formState: { errors, isValid },
  } = useForm<LanderSchemaType>({
    resolver: zodResolver(landerQualifyStepSchema),
    mode: 'onTouched',
    defaultValues: { [InputName.OWN_HOME]: OwnHome.OWN },
  });
  const { classes, cx } = useStyles({
    isOnDark,
    hideImageOnMobile,
  });
  const { classes: globalClasses } = useGlobalStyles({ fixed: false });
  const { isMobile } = useViewPortSize();
  const imageSize = isMobile ? 768 : 1440;

  const onSubmit: SubmitHandler<LanderQualifyStepSchemaType> = (
    data: LanderQualifyStepSchemaType
  ) => {
    nextPage(data);
  };

  return (
    <GridContainer2
      gridProps={{
        className: cx(
          show ? classes.visible : classes.hidden,
          reverseLayout ? classes.reverseLayout : classes.layout
        ),
      }}
      fixed={fixedGrid}
    >
      <Grid2
        xs={12}
        sm={9}
        md={6}
        className={cx(reverseLayout && classes.reverseContent)}
      >
        <div className={cx(globalClasses.chip, classes.step)}>step 1 of 2</div>
        <div
          className={cx(
            fixedGrid ? globalClasses.h2 : globalClasses.h1,
            classes.heading
          )}
        >
          {heading}
        </div>
        <div className={cx(globalClasses.body2, classes.subheading)}>
          {subheading}
        </div>
        <div className={classes.inputGroup}>
          <div className={classes.inputContainer}>
            <FormInput
              label={zipCodeLabel}
              testId='zip-input'
              type={InputType.ZIP}
              inputMode='numeric'
              isOnDark={isOnDark}
              error={errors[InputName.ZIP_CODE]?.message}
              name={InputName.ZIP_CODE}
              register={register}
            />
          </div>
          <div className={classes.inputContainer}>
            <FormInput
              testId='bill-input'
              label={averageElectricBillLabel}
              placeholder={averageElectricBillPlaceholder}
              type={InputType.DOLLAR}
              inputMode='numeric'
              isOnDark={isOnDark}
              error={errors[InputName.LAST_BILL]?.message}
              register={register}
              name={InputName.LAST_BILL}
            />
          </div>
          <div className={classes.radioContainer}>
            {showHomeownerQuestion && (
              <RadioGroup
                legend={ownYourHomeLabel}
                options={[
                  { label: 'Yes', value: OwnHome.OWN },
                  { label: 'No', value: OwnHome.RENT },
                ]}
                register={register}
                trigger={trigger}
                name={InputName.OWN_HOME}
                errors={errors}
                isOnDark={isOnDark}
                gaEvent={{
                  event: 'Form_cta_click',
                  Form_tab: 'Home Ownership',
                  Form_step: 1,
                  click_text: '',
                }}
                isSmall={true}
              />
            )}
          </div>
        </div>
        <Button
          testId='next-button'
          type='submit'
          variant={ButtonVariant.PRIMARY}
          label={submitText}
          size={ButtonSize.MD}
          className={classes.button}
          onClick={handleSubmit(onSubmit)}
          disabled={
            !!errors[InputName.ZIP_CODE] ||
            !!errors[InputName.LAST_BILL] ||
            (showHomeownerQuestion && !!errors[InputName.OWN_HOME]) ||
            !isValid
          }
          isOnDark={isOnDark}
        />
      </Grid2>
      <Grid2 xs={12} md={6} className={classes.hideImageOnMobile}>
        <img
          src={setImgWidth(image, imageSize)}
          srcSet={setSrcSetImg(image, imageSize)}
          className={classes.landerImage}
          alt={altText}
          width={imageSize}
        />
      </Grid2>
    </GridContainer2>
  );
}
